import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';

import { ReactComponent as ArrowDropDown } from '../images/icons/arrow-drop-down.svg';

type DropDownContextType = {
  registerItem: (ref: React.RefObject<HTMLButtonElement>) => void;
};

const DropDownContext = React.createContext<DropDownContextType | null>(null);

const dropDownPadding = 4;

const StyledActivator = styled(Button)(({ theme }) => ({
  paddingLeft: theme.spacing(1.6),
  paddingRight: theme.spacing(4.2),
  paddingTop: theme.spacing(0.4),
  paddingBottom: theme.spacing(0.4),
  border: `1px solid ${theme.palette.neutral[300]}`,
  position: 'relative',
  width: '140px',
  justifyContent: 'flex-start',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  span: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.neutral[900],
  },
  svg: {
    position: 'absolute',
    right: '8px',
  },
})) as typeof Button;

type StyledDropDownItemProps = {
  isActive?: boolean;
};

const StyledDropDownItem = styled(Button, {
  shouldForwardProp: propName => propName !== 'isActive',
})<StyledDropDownItemProps>(({ theme, isActive }) => ({
  maxWidth: '250px',
  minWidth: '100px',
  display: 'flex',
  color: theme.palette.neutral[900],
  padding: theme.spacing(1.1),
  flexDirection: 'row',
  justifyContent: 'space-between',
  margin: `0 ${theme.spacing(1.1)} 0 ${theme.spacing(1.1)}`,
  fontSize: theme.typography.pxToRem(14),
  borderRadius: theme.spacing(1.1),
  // todo - design color request, add to pallette
  backgroundColor: isActive ? 'rgba(223, 232, 250, 0.3)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(223, 232, 250, 0.3)',
  },
  '&:first-child': {
    marginTop: theme.spacing(1.1),
  },
  '&:last-child': {
    marginBottom: theme.spacing(1.1),
  },
  svg: {
    display: 'flex',
    width: '20px',
    userSelect: 'none',
    marginRight: '12px',
  },
  span: {
    display: 'flex',
    flexGrow: 1,
    minWidth: '150px',
  },
})) as React.FC<StyledDropDownItemProps & ButtonProps>;

const StyledDropDownContainer = styled('div')({
  zIndex: 10000,
  position: 'fixed',
  borderRadius: '8px',
  minHeight: '40px',
  background: '#fff',
  boxShadow:
    '0 12px 28px 0 rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
});

export const LexicalDropDownItem = ({
  children,
  isActive = false,
  onClick,
}: {
  children: React.ReactNode;
  isActive?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const ref = useRef<HTMLButtonElement>(null);

  const dropDownContext = React.useContext(DropDownContext);

  if (dropDownContext === null) {
    throw new Error('DropDownItem must be used within a DropDown');
  }

  const { registerItem } = dropDownContext;

  useEffect(() => {
    if (ref && ref.current) {
      registerItem(ref);
    }
  }, [ref, registerItem]);

  return (
    <StyledDropDownItem isActive={isActive} onClick={onClick} ref={ref}>
      {children}
    </StyledDropDownItem>
  );
};

const LexicalDropDownItems = ({
  children,
  dropDownRef,
  onClose,
}: {
  children: React.ReactNode;
  dropDownRef: React.Ref<HTMLDivElement>;
  onClose: () => void;
}) => {
  const [items, setItems] = useState<Array<React.RefObject<HTMLButtonElement>>>();
  const [highlightedItem, setHighlightedItem] = useState<React.RefObject<HTMLButtonElement>>();

  const registerItem = useCallback(
    (itemRef: React.RefObject<HTMLButtonElement>) => {
      setItems(prev => (prev ? [...prev, itemRef] : [itemRef]));
    },
    [setItems],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!items) {
      return;
    }

    const key = event.key;

    if (['Escape', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
      event.preventDefault();
    }

    if (key === 'Escape' || key === 'Tab') {
      onClose();
    } else if (key === 'ArrowUp') {
      setHighlightedItem(prev => {
        if (!prev) {
          return items[0];
        }
        const index = items.indexOf(prev) - 1;
        return items[index === -1 ? items.length - 1 : index];
      });
    } else if (key === 'ArrowDown') {
      setHighlightedItem(prev => {
        if (!prev) {
          return items[0];
        }
        return items[items.indexOf(prev) + 1];
      });
    }
  };

  const contextValue = useMemo(
    () => ({
      registerItem,
    }),
    [registerItem],
  );

  useEffect(() => {
    if (items && !highlightedItem) {
      setHighlightedItem(items[0]);
    }

    if (highlightedItem && highlightedItem.current) {
      highlightedItem.current.focus();
    }
  }, [items, highlightedItem]);

  return (
    <DropDownContext.Provider value={contextValue}>
      <StyledDropDownContainer ref={dropDownRef} onKeyDown={handleKeyDown}>
        {children}
      </StyledDropDownContainer>
    </DropDownContext.Provider>
  );
};

export const LexicalDropDown = ({
  disabled = false,
  buttonLabel,
  children,
  stopCloseOnClickSelf,
}: {
  // eslint-disable-next-line react/boolean-prop-naming
  disabled?: boolean;
  buttonLabel?: string;
  children: ReactNode;
  // eslint-disable-next-line react/boolean-prop-naming
  stopCloseOnClickSelf?: boolean;
}): JSX.Element => {
  const dropDownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showDropDown, setShowDropDown] = useState(false);

  const handleClose = () => {
    setShowDropDown(false);
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  useEffect(() => {
    const button = buttonRef.current;
    const dropDown = dropDownRef.current;

    if (showDropDown && button !== null && dropDown !== null) {
      const { top, left } = button.getBoundingClientRect();
      dropDown.style.top = `${top + button.offsetHeight + dropDownPadding}px`;
      dropDown.style.left = `${Math.min(left, window.innerWidth - dropDown.offsetWidth - 20)}px`;
    }
  }, [dropDownRef, buttonRef, showDropDown]);

  useEffect(() => {
    const button = buttonRef.current;

    if (button !== null && showDropDown) {
      const handle = (event: MouseEvent) => {
        const target = event.target;
        if (stopCloseOnClickSelf) {
          if (dropDownRef.current && dropDownRef.current.contains(target as Node)) {
            return;
          }
        }
        if (!button.contains(target as Node)) {
          setShowDropDown(false);
        }
      };
      document.addEventListener('click', handle);

      return () => {
        document.removeEventListener('click', handle);
      };
    }
  }, [dropDownRef, buttonRef, showDropDown, stopCloseOnClickSelf]);

  useEffect(() => {
    const handleButtonPositionUpdate = () => {
      if (showDropDown) {
        const button = buttonRef.current;
        const dropDown = dropDownRef.current;
        if (button !== null && dropDown !== null) {
          const { top } = button.getBoundingClientRect();
          const newPosition = top + button.offsetHeight + dropDownPadding;
          if (newPosition !== dropDown.getBoundingClientRect().top) {
            dropDown.style.top = `${newPosition}px`;
          }
        }
      }
    };

    document.addEventListener('scroll', handleButtonPositionUpdate);

    return () => {
      document.removeEventListener('scroll', handleButtonPositionUpdate);
    };
  }, [buttonRef, dropDownRef, showDropDown]);

  return (
    <React.Fragment>
      <StyledActivator disabled={disabled} onClick={() => setShowDropDown(!showDropDown)} ref={buttonRef}>
        {buttonLabel && <span>{buttonLabel}</span>}
        <ArrowDropDown />
      </StyledActivator>

      {showDropDown &&
        createPortal(
          <LexicalDropDownItems dropDownRef={dropDownRef} onClose={handleClose}>
            {children}
          </LexicalDropDownItems>,
          document.body,
        )}
    </React.Fragment>
  );
};
